import {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useMe} from '../../../common/hooks/useMe'
import {
  generateCreativeAssetZip,
  hasCreativeContent,
  type CreativeAssetState,
} from '../../../common/utils/creativeAssetRenderer'
import {CREATIVE_PLACEMENTS, type CreativeImageFormat} from '../../../common/constants/creativeAssetSpecs'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {reportError} from '../../../common/errors/reportError'
import {downloadFile} from '../../../common/utils/downloadFile'
import ConverterExportDialog from '../converterShared/ConverterExportDialog'
import {CONVERTER_AD_FEATS} from '../converterShared/adFeatIcons'

interface CreativeDownloadPanelProps {
  state: CreativeAssetState
}

const FORMATS: CreativeImageFormat[] = ['png', 'jpeg']
const ZIP_FILENAME = 'app-store-creative-assets.zip'

/** Everything one export needs, frozen when the upsell dialog opens. */
interface ZipJob {
  state: CreativeAssetState
  format: CreativeImageFormat
}

const CreativeDownloadPanel = ({state}: CreativeDownloadPanelProps) => {
  const {t} = useTranslation()
  const {me} = useMe()
  const [format, setFormat] = useState<CreativeImageFormat>('png')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set when the upsell dialog opens: later edits must not restart the export running inside it.
  const [dialogJob, setDialogJob] = useState<ZipJob | null>(null)

  const hasCredits = me !== null && me.credits > 0
  const ready = hasCreativeContent(state)

  const handleFormatChange = useCallback((next: CreativeImageFormat) => {
    setFormat(next)
    trackEvent('creative_assets_format_changed', {format: next})
  }, [])

  const handleClick = useCallback(async () => {
    if (!ready) return
    trackEvent('creative_assets_download_clicked', {has_credits: hasCredits, format})
    if (!hasCredits) {
      setDialogJob({state, format})
      return
    }
    trackEvent('creative_assets_export_started', {gated: false})
    setBusy(true)
    setError(null)
    try {
      const blob = await generateCreativeAssetZip(state, format)
      await downloadFile(blob, ZIP_FILENAME)
      trackEvent('creative_assets_export_completed', {size_bytes: blob.size, gated: false})
    } catch (err) {
      reportError(err, 'creative-assets-export', {format})
      setError(t('creativeAssets.download.failed'))
    } finally {
      setBusy(false)
    }
  }, [ready, hasCredits, state, format, t])

  const handleDialogGenerate = useCallback(
    () =>
      dialogJob
        ? generateCreativeAssetZip(dialogJob.state, dialogJob.format)
        : Promise.reject(new Error('no export job')),
    [dialogJob],
  )
  const handleDialogClose = useCallback(() => setDialogJob(null), [])

  return (
    <div className="caa-download">
      <div className="caa-tabs caa-tabs-compact">
        {FORMATS.map((f) => (
          <button
            key={f}
            type="button"
            className={`caa-tab${format === f ? ' is-active' : ''}`}
            onClick={() => handleFormatChange(f)}
          >
            {f.toUpperCase()}
          </button>
        ))}
      </div>
      <p className="cvt-hint">{t('creativeAssets.download.formatHint')}</p>

      <ul className="caa-file-list">
        {CREATIVE_PLACEMENTS.map((spec) => (
          <li key={spec.id}>
            <span>{t(`creativeAssets.placements.${spec.id}`)}</span>
            <span className="caa-file-size">
              {spec.width} × {spec.height} · {spec.formats.includes(format) ? format.toUpperCase() : 'PNG'}
            </span>
          </li>
        ))}
      </ul>

      <button type="button" className="cvt-btn cvt-btn-primary" onClick={handleClick} disabled={busy || !ready}>
        {busy ? t('creativeAssets.download.busy') : t('creativeAssets.download.cta')}
      </button>
      {error && <p className="cvt-error">{error}</p>}
      {!ready && <p className="cvt-hint">{t('creativeAssets.download.needContent')}</p>}
      <p className="cvt-hint">{t('creativeAssets.download.noAlpha')}</p>

      {dialogJob && (
        <ConverterExportDialog
          namespace="creativeAssets"
          breadcrumbCategory="creative-assets"
          analyticsSurface="creative_assets"
          startedEvent="creative_assets_export_started"
          completedEvent="creative_assets_export_completed"
          filename={ZIP_FILENAME}
          showAd
          adFeats={CONVERTER_AD_FEATS}
          adCtaTo="/free"
          onGenerate={handleDialogGenerate}
          onClose={handleDialogClose}
        />
      )}
    </div>
  )
}

export default CreativeDownloadPanel
